import { Card, CardBody, CardHeader } from "@heroui/react";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";
import { Content } from "../components/content";
import { MainLayout } from "../components/mainlayout";
import { NavigationButtons } from "../components/navigation-button";
import { useResumeBuilder } from "../context";
import { useNavigateWithParams } from "../hooks";
import type { JobExperience, LanguageSkill, Training } from "../types";
import { PlusLineButton } from "./plusline-button";
import { ExperienceModal } from "./experience-modal";
import { ItemList } from "./item-list";
import { additionalSkills, drivingLicenses, hobbies, languageSkillLevelMap, softwareSkills } from "./constants";
import { LanguageModal } from "./language-modal";

const ResumeBuilder2Page = () => {
    const { resumeContent, updateStep2 } = useResumeBuilder();
    const navigate = useNavigateWithParams();
    const [languageModalIsOpen, setLanguageModalIsOpen] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<"experience" | "training">(
        "experience",
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null,);
    const [experienceData, setExperienceData] = useState<JobExperience[]>(
        resumeContent.step2.experienceData,
    );

    const [trainingData, setTrainingData] = useState<Training[]>(
        resumeContent.step2.trainingData,
    );

    const [currentData, setCurrentData] = useState<
        JobExperience | Training | null
    >(null);
    const [editIndex, setEditIndex] = useState<number | null>(null);

    const openModal = (
        type: "experience" | "training",
        index: number | null = null,
    ) => {
        setModalType(type);
        setEditIndex(index);
        setCurrentData(
            index !== null
                ? type === "experience"
                    ? experienceData[index]
                    : trainingData[index]
                : null,
        );
        setModalOpen(true);
    };

    useEffect(() => {
        setExperienceData(resumeContent.step2.experienceData);
        setTrainingData(resumeContent.step2.trainingData);
    }, [resumeContent.step2.experienceData, resumeContent.step2.trainingData]);

    const closeModal = () => setModalOpen(false);

    const handleSubmit = (data: JobExperience | Training) => {
        if (editIndex !== null) {
            updateDataArray(data, editIndex);
        } else {
            appendDataArray(data);
        }
        closeModal();
    };

    const addLanguage = (data: LanguageSkill) => {
        const existingLanguages = resumeContent.step2.languages || [];

        const languageExists = existingLanguages.some(
            (lang) => lang.language === data.language,
        );

        if (languageExists) {
            return;
        }

        const updatedLanguages = [...existingLanguages, data];
        updateStep2({
            languages: updatedLanguages,
        });
    }

    const deleteLanguage = (index: number) => {
        const existingLanguages = resumeContent.step2.languages || [];
        const updatedLanguages = existingLanguages.filter(
            (_, i) => i !== index,
        );

        updateStep2({
            languages: updatedLanguages,
        });
    }

    const updateDataArray = (
        updatedData: JobExperience | Training,
        index: number,
    ) => {
        if (modalType === "experience") {
            const updatedExperience = updateAndSortArray(
                experienceData,
                updatedData as JobExperience,
                index,
            );
            setExperienceData(updatedExperience);
            updateStep2({
                experienceData: updatedExperience,
            });
        } else if (modalType === "training") {
            const updatedTraining = updateAndSortArray(
                trainingData,
                updatedData as Training,
                index,
            );
            setTrainingData(updatedTraining);
            updateStep2({
                trainingData: updatedTraining,
            });
        }
    };

    const sortByEndDate = (
        a: JobExperience | Training,
        b: JobExperience | Training,
    ) => {
        if (a.isCurrent && !b.isCurrent) {
            return -1;
        }

        if (!a.isCurrent && b.isCurrent) {
            return 1;
        }

        if (a.isCurrent && b.isCurrent) {
            return b.startDate.getTime() - a.startDate.getTime();
        }

        if (!(a.endDate && b.endDate)) {
            return 0;
        }

        if (b.endDate.getTime() !== a.endDate.getTime()) {
            return b.endDate.getTime() - a.endDate.getTime();
        }

        return b.startDate.getTime() - a.startDate.getTime();
    };

    const appendDataArray = (newData: JobExperience | Training) => {
        if (modalType === "experience") {
            const newExperienceData = [
                ...experienceData,
                newData as JobExperience,
            ].sort(sortByEndDate);

            setExperienceData(newExperienceData);
            updateStep2({
                experienceData: newExperienceData,
            });
            setErrorMessage(null);
        } else if (modalType === "training") {
            const newTrainingData = [...trainingData, newData as Training].sort(
                sortByEndDate,
            );

            setTrainingData(newTrainingData);
            updateStep2({
                trainingData: newTrainingData,
            });
            setErrorMessage(null);
        }
    };

    const updateAndSortArray = <T extends JobExperience | Training>(
        arr: T[],
        data: T,
        index: number,
    ): T[] => {
        const updatedArray = [...arr];
        updatedArray[index] = data;
        return updatedArray.sort(sortByEndDate);
    };

    const proceedToNextStep = () => {
        if (!experienceData.length && !trainingData.length) {
            setErrorMessage("Mindestens ein Eintrag in Berufserfahrung oder Ausbildung erforderlich.");
            return;
        }
        setErrorMessage(null);
        updateStep2({
            experienceData,
            trainingData,
        });
        navigate("/step3");
    };

    const deleteEntry = (index: number, type: "experience" | "training") => {
        if (type === "experience") {
            const updated = experienceData.filter((_, i) => i !== index);
            setExperienceData(updated);
            updateStep2({ experienceData: updated });
        } else if (type === "training") {
            const updated = trainingData.filter((_, i) => i !== index);
            setTrainingData(updated);
            updateStep2({ trainingData: updated });
        }
    };

    const previewChanges = () => {
        updateStep2({
            experienceData,
            trainingData,
        });
    };

    return (
        <MainLayout>
            <Content
                title="Ausbildung, Berufserfahrung & Sonstiges"
                currentStep={2}
            >
                <div className="flex flex-col gap-5">
                    <SectionCard title="Berufserfahrung (alle Stationen)">
                        <ExperienceSection
                            experienceData={experienceData}
                            onEdit={openModal}
                            onDelete={deleteEntry}
                            onAdd={() => openModal("experience")}
                        />
                    </SectionCard>

                    <SectionCard title="Ausbildung">
                        <TrainingSection
                            trainingData={trainingData}
                            onEdit={openModal}
                            onDelete={deleteEntry}
                            onAdd={() => openModal("training")}
                        />
                    </SectionCard>

                    <SectionCard title="Zusatzqualifikationen">
                        <ItemList
                            items={additionalSkills}
                            initialValues={
                                resumeContent.step2?.additionalSkills
                            }
                            selectPlaceholder="Aus Vorlage wählen"
                            addCustomInputPlaceholder="Zusatzqualifikation"
                            addCustomText="Eigene hinzufügen"
                            addCustomShortText="Eigene"
                            onChange={(values) =>
                                updateStep2({
                                    additionalSkills: [...values] as string[],
                                })
                            }
                        />
                    </SectionCard>

                    <SectionCard title="Softwarekenntnisse">
                        <ItemList
                            items={softwareSkills}
                            initialValues={resumeContent.step2?.softwareSkills}
                            selectPlaceholder="Aus Vorlage wählen"
                            addCustomInputPlaceholder="Softwarekenntniss"
                            addCustomText="Eigene hinzufügen"
                            addCustomShortText="Eigene"
                            onChange={(values) =>
                                updateStep2({
                                    softwareSkills: [...values] as string[],
                                })
                            }
                        />
                    </SectionCard>

                    <SectionCard title="Führerscheine">
                        <ItemList
                            items={drivingLicenses}
                            initialValues={resumeContent.step2?.drivingLicenses}
                            selectPlaceholder="Aus Vorlage wählen"
                            addCustomInputPlaceholder="Führerschein"
                            addCustomText="Eigenen hinzufügen"
                            addCustomShortText="Eigenen"
                            onChange={(values) =>
                                updateStep2({
                                    drivingLicenses: [...values] as string[],
                                })
                            }
                        />
                    </SectionCard>

                    <SectionCard title="Hobbies">
                        <ItemList
                            items={hobbies}
                            initialValues={resumeContent.step2?.hobbies}
                            selectPlaceholder="Aus Vorlage wählen"
                            addCustomInputPlaceholder="Hobby"
                            addCustomText="Eigenes hinzufügen"
                            addCustomShortText="Eigenes"
                            onChange={(values) =>
                                updateStep2({
                                    hobbies: [...values] as string[],
                                })
                            }
                        />
                    </SectionCard>

                    <SectionCard title="Sprachkenntnisse">
                        <LanguageSection
                            languageSkills={resumeContent.step2.languages}
                            onAdd={() => setLanguageModalIsOpen(true)}
                            onDelete={deleteLanguage}
                        />
                    </SectionCard>

                    {errorMessage && (
                        <div className="flex w-full items-center justify-center">
                            <p className="text-danger text-sm">
                                {errorMessage}
                            </p>
                        </div>
                    )}

                    <NavigationButtons
                        onBack={() => navigate(-1)}
                        onPreview={previewChanges}
                        onNext={proceedToNextStep}
                    />
                </div>
            </Content>

            <ExperienceModal
                title={
                    modalType === "experience"
                        ? "Berufserfahrung"
                        : "Ausbildung"
                }
                isOpen={isModalOpen}
                onClose={closeModal}
                onFormSubmit={handleSubmit}
                initialData={currentData}
                type={modalType}
            />
            <LanguageModal
                title="Sprachkenntnisse"
                isOpen={languageModalIsOpen}
                onClose={() => setLanguageModalIsOpen(false)}
                onFormSubmit={addLanguage}
            />
        </MainLayout>
    );
};

interface ExperienceSectionProps {
    experienceData: JobExperience[];
    onEdit: (type: "experience", index: number) => void;
    onDelete: (index: number, type: "experience") => void;
    onAdd: () => void;
}

const ExperienceSection = ({
    experienceData,
    onEdit,
    onDelete,
    onAdd,
}: ExperienceSectionProps) => (
    <div className="flex flex-col gap-6">
        {experienceData?.map((item, index) => (
            <DataEntry
                key={`experience-${item.position}-${item.company}-${item.startDate}`}
                item={item}
                index={index}
                type="experience"
                onEdit={onEdit}
                onDelete={onDelete}
                titleField="position"
            />
        ))}
        <PlusLineButton onClick={onAdd} />
    </div>
);

interface TrainingSectionProps {
    trainingData: Training[];
    onEdit: (type: "training", index: number) => void;
    onDelete: (index: number, type: "training") => void;
    onAdd: () => void;
}

const TrainingSection = ({
    trainingData,
    onEdit,
    onDelete,
    onAdd,
}: TrainingSectionProps) => (
    <div className="flex flex-col gap-6">
        {trainingData?.map((item, index) => (
            <DataEntry
                key={`training-${item.title}-${item.company}-${item.startDate}`}
                item={item}
                index={index}
                type="training"
                onEdit={onEdit}
                onDelete={onDelete}
                titleField="title"
            />
        ))}
        <PlusLineButton onClick={onAdd} />
    </div>
);

interface LanguageSectionProps {
    languageSkills: LanguageSkill[];
    onDelete: (index: number) => void;
    onAdd: () => void;
}

const LanguageSection = ({
    languageSkills,
    onDelete,
    onAdd,
}: LanguageSectionProps) => (
    <div className="flex flex-col gap-6">
        {languageSkills?.map((item, index) => (
            <div key={item.language} className="flex justify-between">
                <div className="flex flex-col">
                    <div>{item.language}</div>
                    <div className="text-default-500 text-sm">
                        {languageSkillLevelMap[item.level] || "-"}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onDelete(index)}
                    className="cursor-pointer text-red-500 transition hover:text-red-700"
                >
                    <IconTrash stroke={1.5} size={20} />
                </button>
            </div>
        ))}
        <PlusLineButton onClick={onAdd} />
    </div>
);

interface DataEntryProps {
    item: JobExperience | Training;
    index: number;
    type: "experience" | "training";
    onEdit: (type: "experience" | "training", index: number) => void;
    onDelete: (index: number, type: "experience" | "training") => void;
    titleField: "position" | "title";
}

const DataEntry = ({
    item,
    index,
    type,
    onEdit,
    onDelete,
    titleField,
}: DataEntryProps) => {
    const formatMonthYear = (dateStr: string | Date | undefined) => {
        if (!dateStr) {
            return "";
        }

        const date = new Date(dateStr);
        return `${date.getMonth() + 1}/${date.getFullYear()}`;
    };

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2 rounded">
                <div className="flex gap-2 font-bold text-md">
                    {item[titleField]}
                </div>
                <div className="flex space-x-2">
                    <button
                        type="button"
                        onClick={() => onEdit(type, index)}
                        className="cursor-pointer text-default-500 transition hover:text-blue-700"
                    >
                        <IconEdit stroke={1.5} size={20} />
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(index, type)}
                        className="cursor-pointer text-red-500 transition hover:text-red-700"
                    >
                        <IconTrash stroke={1.5} size={20} />
                    </button>
                </div>
            </div>
            <div className="flex justify-between gap-2 text-default-500 text-sm xs:text-base">
                <span className="overflow-hidden text-ellipsis text-nowrap">
                    {item.company}
                </span>
                <span className="text-nowrap">
                    {`${formatMonthYear(item.startDate)} - ${item.isCurrent ? "Aktuell" : formatMonthYear(item.endDate)}`}
                </span>
            </div>
        </div>
    );
};

const SectionCard = ({
    title,
    children,
}: {
    title: string;
    children: ReactNode;
}) => (
    <Card
        shadow="none"
        classNames={{
            base: "border border-default-200",
        }}
    >
        <CardHeader className="p-4 pb-0">
            <h2 className="font-medium text-lg">{title}</h2>
        </CardHeader>
        <CardBody className="p-4">{children}</CardBody>
    </Card>
);

export { ResumeBuilder2Page };
